import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Avatar,
  IconButton,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { userProfileService } from '../services/userProfileService';
import { userService } from '../services/userService';
import { UserProfile as UserProfileType, UpdateUserProfileRequest, UserRole, PreferredTrainingInfo, ChangePasswordRequest } from '../types/user';
import { useAuth } from '../contexts/AuthContext';
import { DOB_MIN, dateOfBirthError, dobMax } from '../utils/dateOfBirth';
import { ageOn } from '../utils/profileUtils';
import UserNotes from './UserNotes';
import { API_BASE_URL } from '../utils/config';
import { formatServerDate } from '../utils/dateUtils';
// [3. javító kör, F-1, 2026-10-03] lejárat-tudatos burkolók (`utils/saveErrors.ts`)
import { requestErrorMessage, saveErrorMessage } from '../utils/saveErrors';
// [Web UX 2. csomag – Belépés és munkamenet, 2026-10-03] A `fetch`-ágak 401-ét semmilyen axios-interceptor
// nem látja (leltár 2(a)): minden hitelesített hívás után a közös jelzőnek adjuk át (`utils/session.ts`).
// [Web 7 / T1, 2026-10-05] a hitelesített `fetch`-ek a közös `authFetch` burkolón mennek: token, csendes
// megújítás, 401 utáni egyszeri ismétlés és a lejárat-jelzés (`reportFetchUnauthorized`) EGY helyen.
import { authFetch } from '../utils/authFetch';
import { useFeedback } from '../contexts/FeedbackContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../utils/formDirty';
import { isForintInputAccepted, parseForintInput, forintFieldValue, isUnfinishedForintDraft, unfinishedForintMessage } from '../utils/money';
// A JELSZÓ-SZABÁLY KLIENS-OLDALI TÜKRE [B-W7, 2026-09-16]. A súgó és az előellenőrzés UGYANABBÓL
// a modulból jön, mint a regisztráció (`utils/passwordPolicy.ts`), és az a modul a SZERVER
// `DTOs/PasswordPolicy.cs`-éből van tükrözve — így a felhasználó ELŐRE megtudja, mi kell, és nem
// a szerver 400-ából, próbálgatással.
import { NEW_PASSWORD_RULE_MESSAGE, newPasswordPolicyError } from '../utils/passwordPolicy';
// A szerepkör-döntések EGY helyen, a GÉPI névből [B-W4, 2026-09-16] — lásd `utils/roles.ts`.
import { isCoachRole } from '../utils/roles';

interface UserProfileProps {
  editUserId?: string; // Admin can pass a user ID to edit another user's profile
  onProfileSaved?: () => void; // Callback when profile is saved (to refresh badge)
}

const DAY_ORDER: Record<string, number> = {
  'Monday': 0, 'Tuesday': 1, 'Wednesday': 2, 'Thursday': 3,
  'Friday': 4, 'Saturday': 5, 'Sunday': 6,
};

const DAY_NAMES_HU: Record<string, string> = {
  'Monday': 'Hétfő', 'Tuesday': 'Kedd', 'Wednesday': 'Szerda', 'Thursday': 'Csütörtök',
  'Friday': 'Péntek', 'Saturday': 'Szombat', 'Sunday': 'Vasárnap',
};

const UserProfile: React.FC<UserProfileProps> = ({ editUserId, onProfileSaved }) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const confirm = useConfirm();
  const isAdmin = user?.userRole === 'Administrator';
  const isEditingOtherUser = isAdmin && editUserId && editUserId !== user?.userId;

  // Administrator can edit any user profile, users can edit their own profile
  const canEdit = isAdmin || !isEditingOtherUser;

  const [profile, setProfile] = useState<UserProfileType | null>(null);
  const [groupTrainings, setGroupTrainings] = useState<PreferredTrainingInfo[]>([]);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [formData, setFormData] = useState<UpdateUserProfileRequest>({
    name: '',
    email: '',
    phone: '',
    venueId: undefined,
    preferredTrainingIds: [],
    bankAccount: '',
    billingName: '',
    billingZipCode: '',
    billingCity: '',
    billingStreetAddress: '',
    taxNumber: '',
    hourlyRate: undefined,
    dateOfBirth: '',
    parentName: '',
    parentPhone: '',
    address: '',
    userRoleId: undefined,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  /**
   * A BETÖLTÖTT profil, mint alapvonal: ehhez hasonlítjuk az űrlapot, hogy tudjuk, van-e
   * mentetlen módosítás. `null` = még nem töltött be, ilyenkor SOHA nem piszkos (a kezdeti üres
   * mezők nem felhasználói bevitel). Mentés után a `loadProfile()` újra beállítja, tehát a
   * sikeres mentés automatikusan „letisztítja" az űrlapot.
   */
  const [baseline, setBaseline] = useState<UpdateUserProfileRequest | null>(null);
  /**
   * Az ÓRADÍJ mezőbe gépelt nyers szöveg — CSAK MEGJELENÍTÉSRE [BL-2b, 2026-09-15].
   *
   * MIÉRT KELL: a mező állapota `number | undefined`, tehát egy RÉGI, tizedes óradíj
   * takarításának első leütése (`"2.8"` → `"2."`) a `parseForintInput`-on `undefined` lett, és a
   * mező KIÜRÜLT — miközben a szerveren még a 2,8 állt. Adminként MÁS felhasználó adatlapját is
   * ez a képernyő szerkeszti (`editUserId` → `updateUserProfileById`), és ott a szerver FELTÉTEL
   * NÉLKÜL ír (`UserProfileController.cs:404`), tehát a félbehagyott takarítás mentése az edző
   * óradíját NULL-ra írta volna. A mentendő SZÁM változatlanul a `formData`-ban van, tehát a
   * mentetlen-változás alapvonalának alakja nem változott.
   */
  const [hourlyRateDraft, setHourlyRateDraft] = useState<string | undefined>(undefined);
  /** Az Óradíj mező DOM-eleme — a mentés-őr ide fókuszál és ide görget [M2]. */
  const hourlyRateInputRef = useRef<HTMLInputElement | null>(null);
  /** A mezőben MEGJELENÍTETT szöveg: a gépelt nyers alak, ha van, különben a mentendő szám. */
  const hourlyRateText = () => hourlyRateDraft ?? forintFieldValue(formData.hourlyRate);

  /**
   * A JELSZÓ-al-dialógus mentetlen adata [NB-2, 2026-09-13].
   *
   * A három jelszó-mező NEM része az adatlap alapvonal-összevetésének (külön `passwordForm`
   * állapot), ezért az al-dialógus bezárása — ESC, backdrop, „Mégse" — eddig KÉRDÉS NÉLKÜL
   * eldobta a beírt jelszavakat. Az adatlap `Adatlap` bejelentését sem lehet erre használni: a
   * fül-váltás kérdése egészen más döntés, mint egy al-dialógus bezárása. Ezért itt a záró út
   * maga kapuz a KÖZÖS megerősítő ablakkal.
   *
   * ⚠️ AZ `isDirty`-BE IS BEKERÜL [BL-4, 2026-09-13]: a záró-út kapuzása csak a KATTINTÓS
   * kilépést fedi. A NEM-KATTINTÓS kilépést (F5, Ctrl+R, lapbezárás) egyedül a `beforeunload`
   * fogja, az pedig KIZÁRÓLAG akkor áll fel, ha a képernyő mentetlen adatot JELENT BE — tehát
   * enélkül az újratöltés kérdés nélkül eldobta a beírt jelszavakat. (Ugyanez volt a mért
   * blokkoló a jelenléti ív megjegyzés-dialógusán.)
   *
   * A „nyitott al-dialógus nem blokkolhatja a fül-váltást" ellenérv MÉRHETŐEN alaptalan: a
   * nyitott MUI modális mögött a felület `aria-hidden`, tehát a fül-gomb nem is elérhető. A
   * `passwordDialogOpen` feltétel pedig a SZELLEM-PISZKOS állapotot zárja ki: mindkét záró út
   * (`closePasswordDialog`, `handlePasswordChange`) nullázza a mezőket, és zárt dialógus
   * semmiképp nem jelent be mentetlen adatot.
   */
  const passwordFormDirty = () =>
    Boolean(passwordForm.currentPassword || passwordForm.newPassword || passwordForm.confirmPassword);

  const closePasswordDialog = async () => {
    if (passwordSaving) return;
    if (passwordFormDirty() && !(await confirm(discardChangesConfirm('jelszó módosítása')))) return;
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError('');
    setPasswordDialogOpen(false);
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    if (!passwordForm.newPassword || !passwordForm.currentPassword) {
      setPasswordError('Minden mező kitöltése kötelező');
      return;
    }
    /**
     * A TELJES SZABÁLY ellenőrzése, nem csak a hossz [B-W7, 2026-09-16].
     *
     * MI VOLT ITT: `newPassword.length < 6`. A szerver 2026-09-15 óta erősséget is kér
     * (kisbetű, nagybetű, szám, nem betű/szám karakter) — a hossz-only ellenőrzés tehát
     * ÁTENGEDTE a `aaaaaaaaaa`-t, a felhasználó pedig a szerver 400-ából tudta meg, mi a baj.
     *
     * ⚠️ AZ INVARIÁNS: a kliens SOHA nem lehet szigorúbb a szervernél — ezért NEM saját
     * feltételeket írunk ide, hanem a szerver-tükröt hívjuk (`utils/passwordPolicy.ts`, ott a
     * mért eltérés-lista). A végső őr továbbra is a szerver: a 400-as magyar mondata a
     * `setPasswordError(saveErrorMessage(...))`-en (belül `apiErrorMessage`) változatlanul kijut.
     */
    const policyError = newPasswordPolicyError(passwordForm.newPassword);
    if (policyError) {
      setPasswordError(policyError);
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Az új jelszavak nem egyeznek');
      return;
    }
    try {
      setPasswordSaving(true);
      await userService.changePassword(user!.userId, passwordForm as ChangePasswordRequest);
      // Toast + azonnali bezárás — a korábbi „maradj nyitva 1.5s-ig, mutass Alert-et" mintát
      // az egységes, alul felbukkanó visszajelzés váltja (user-szabály: siker után nincs OK-ablak).
      showSuccess('A jelszó módosítva.');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordDialogOpen(false);
    } catch (err: any) {
      setPasswordError(saveErrorMessage(err, 'Hiba történt a jelszó módosításakor'));
    } finally {
      setPasswordSaving(false);
    }
  };

  useEffect(() => {
    loadProfile();
    loadGroupTrainings();
    if (isAdmin && isEditingOtherUser) {
      loadRoles();
    }
  }, [editUserId]);

  const loadGroupTrainings = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/planned-events/group-trainings`);
      if (!response.ok) {
        console.error('Failed to load group trainings:', response.status);
        setGroupTrainings([]);
        return;
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setGroupTrainings(data);
      } else {
        setGroupTrainings([]);
      }
    } catch (err) {
      console.error('Error loading group trainings:', err);
      setGroupTrainings([]);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/user-roles`);

      if (!response.ok) return;

      const result = await response.json();
      const rolesArray = result.data || result;
      if (Array.isArray(rolesArray)) {
        const mappedRoles = rolesArray.map((r: any) => ({
          userRoleId: r.UserRoleId || r.userRoleId,
          name: r.Name || r.name,
          displayName: r.DisplayName || r.displayName,
          displayOrder: r.DisplayOrder || r.displayOrder || 0,
          isActive: r.IsActive ?? r.isActive ?? true,
        }));
        setRoles(mappedRoles);
      }
    } catch (err) {
      console.error('Error loading roles:', err);
    }
  };

  const loadProfile = async () => {
    setLoading(true);
    setError('');
    // Betöltés közben SOHA nincs mentetlen adat: az előző felhasználó alapvonala nem érvényes
    // az új profilra (az admin másik játékos adatlapjára váltása is ide jön be).
    setBaseline(null);
    /**
     * A PÉNZ-VÁZLAT NULLÁZÁSA A BETÖLTÉS ELŐTT IS [S2, 2026-09-15].
     *
     * MI VOLT A BAJ: a nullázás CSAK az `await` UTÁN, a sikeres ágon futott. Ha a betöltés
     * ELBUKOTT (hálózat, 500), a KORÁBBI felhasználó félbehagyott, gépelt óradíj-szövege ott
     * maradt a mezőben — egy MÁSIK profil adatai fölött („szellem-vázlat"), és a mentés azt
     * küldte volna ki. A `UserManagement` már a dialógus MEGNYITÁSAKOR nulláz; itt is ez a helyes
     * pont, a sikeres ági nullázás pedig marad (a betöltött érték is elavulttá teszi a vázlatot).
     */
    setHourlyRateDraft(undefined);
    try {
      let data: UserProfileType;
      if (isEditingOtherUser && editUserId) {
        data = await userProfileService.getUserProfileById(editUserId);
      } else {
        data = await userProfileService.getMyProfile();
      }
      setProfile(data);

      // Populate form
      const loaded: UpdateUserProfileRequest = {
        name: data.name,
        // [C10, 2026-10-30] a profil-típusban `string | null` (más tag adatlapján SOS nélkül a szerver
        // `null`-t ad, `backend/PinyoApi/Services/UserProfileSanitizer.cs`). Ez a képernyő a SAJÁT
        // adatlapot vagy adminként másét tölti — ott mindig kitöltött; a `?? ''` a vezérelt mező
        // `null` értéke (React-figyelmeztetés, „null" szöveg) ellen véd.
        email: data.email ?? '',
        phone: data.phone ?? '',
        venueId: data.venueId || undefined,
        preferredTrainingIds: data.preferredTrainingIds || [],
        bankAccount: data.bankAccount || '',
        billingName: data.billingName || '',
        billingZipCode: data.billingZipCode || '',
        billingCity: data.billingCity || '',
        billingStreetAddress: data.billingStreetAddress || '',
        taxNumber: data.taxNumber || '',
        hourlyRate: data.hourlyRate,
        dateOfBirth: data.dateOfBirth ? data.dateOfBirth.split('T')[0] : '',
        parentName: data.parentName || '',
        parentPhone: data.parentPhone || '',
        address: data.address || '',
        userRoleId: data.userRoleId || undefined,
      };
      setFormData(loaded);
      // A vázlat a BETÖLTÖTT értékkel elavul — enélkül egy régi gépelt szöveg maradna a mezőben.
      setHourlyRateDraft(undefined);
      // Az alapvonal a betöltés UTÁN, PONTOSAN azzal az objektummal, amit az űrlapba tettünk —
      // így a betöltés önmagában nem tud piszkos állapotot előállítani.
      setBaseline(loaded);
    } catch (err: any) {
      console.error('Error loading profile:', err.response?.status ?? 'nincs válasz', err);
      setError(requestErrorMessage(err, 'Hiba történt a profil betöltésekor'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // PÉNZ-MEZŐ: csak SZÁMJEGY, EGÉSZ forint [BL-2/N-8, 2026-09-14]. A `parseFloat` a magyarul
    // gépelt `2.800`-ból (kétezer-nyolcszáz) 2,8 Ft-ot csinált, NÉMÁN — az indoklás és a mérés a
    // `utils/money.ts` fejlécében. A nem illeszkedő leütést el sem tároljuk.
    // A MOSTANI tartalmat is átadjuk, hogy egy RÉGI, tizedes érték takarítható legyen [J-3].
    const isForintField = name === 'hourlyRate';
    // A MOSTANI tartalom a MEGJELENÍTETT szöveg (a vázlat, ha van), nem a szám-állapot [BL-2b].
    if (isForintField && !isForintInputAccepted(value, hourlyRateText())) return;
    // A gépelt nyers alak LÁTSZIK (a félbe-takarított „2." is); a mentendő szám külön áll.
    if (isForintField) setHourlyRateDraft(value);

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    setFormData((prev) => ({
      ...prev,
      [name]: isForintField
        ? parseForintInput(value)
        : name === 'dateOfBirth'
          ? (value || '')
          : value,
    }));
  };

  const handleTrainingToggle = (plannedEventId: string) => {
    setFormData((prev) => {
      const current = prev.preferredTrainingIds || [];
      const updated = current.includes(plannedEventId)
        ? current.filter(id => id !== plannedEventId)
        : [...current, plannedEventId];
      return { ...prev, preferredTrainingIds: updated };
    });
  };

  // Egy konkrét mező validálása (onBlur eseményhez)
  const validateField = (fieldName: string, value: string | undefined): string | null => {
    switch (fieldName) {
      case 'name':
        if (!value?.trim()) {
          return 'A név megadása kötelező';
        }
        return null;

      case 'email':
        if (!value?.trim()) {
          return 'Az e-mail cím megadása kötelező';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Érvénytelen e-mail cím formátum';
        }
        return null;

      case 'phone':
        if (!value?.trim()) {
          return 'A telefonszám megadása kötelező';
        } else if (!/^\+36\d{9}$/.test(value.replace(/\s/g, ''))) {
          return 'Érvénytelen telefonszám formátum. Helyes formátum: +36201234567';
        }
        return null;

      case 'dateOfBirth':
        return dateOfBirthError(value ?? '') || null;

      case 'parentName':
        if (isUnder18 && !value?.trim()) {
          return '18 év alatti játékosok esetén kötelező a szülő nevének megadása';
        }
        return null;

      case 'parentPhone':
        if (isUnder18) {
          if (!value?.trim()) {
            return '18 év alatti játékosok esetén kötelező a szülő telefonszámának megadása';
          } else if (!/^\+36\d{9}$/.test(value.replace(/\s/g, ''))) {
            return 'Érvénytelen telefonszám formátum. Helyes formátum: +36201234567';
          }
        }
        return null;

      default:
        return null;
    }
  };

  const handleFieldBlur = (fieldName: string) => {
    const value = formData[fieldName as keyof typeof formData] as string;
    const error = validateField(fieldName, value);

    setFieldErrors((prev) => ({
      ...prev,
      [fieldName]: error || '',
    }));
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    // Kötelező mezők validációja
    const nameError = validateField('name', formData.name);
    if (nameError) errors.name = nameError;

    const emailError = validateField('email', formData.email);
    if (emailError) errors.email = emailError;

    const phoneError = validateField('phone', formData.phone);
    if (phoneError) errors.phone = phoneError;

    const dateOfBirthError = validateField('dateOfBirth', formData.dateOfBirth);
    if (dateOfBirthError) errors.dateOfBirth = dateOfBirthError;

    // 18 év alattiak esetén szülő adatai kötelezőek
    if (isUnder18) {
      const parentNameError = validateField('parentName', formData.parentName);
      if (parentNameError) errors.parentName = parentNameError;

      const parentPhoneError = validateField('parentPhone', formData.parentPhone);
      if (parentPhoneError) errors.parentPhone = parentPhoneError;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    /**
     * KÖZTES PÉNZ-VÁZLAT: a mentés MEGÁLL, a mező megnevezésével [BL-2b, 2026-09-15].
     * A félbe-takarított óradíj (`"2."`) `undefined`-ként ment volna ki, és a szerver a TÁROLT
     * értéket NULL-ra írta volna. A SZÁNDÉKOS törlés (üres mező) érintetlen: az számjegy-alakú.
     */
    if (isUnfinishedForintDraft(hourlyRateDraft)) {
      /**
       * ⚠️ A BLOKKOLÁS NEM LEHET NÉMA [M2, 2026-09-15]. A `setError` a lap TETEJÉN lévő
       * Alertbe megy (`:611`), az Óradíj mező viszont a „Számlázási adatok" szakaszban, jóval
       * lejjebb (`:850`) — hosszú űrlapon a felhasználó a Mentés megnyomása után SEMMIT nem
       * látott, tehát a mentés néma no-opnak tűnt. Ezért a hiba HÁROM helyen jelenik meg: a
       * felső sáv, egy TOAST, és MAGA A MEZŐ (piros keret + súgó), amit oda is görgetünk.
       */
      const message = unfinishedForintMessage('Óradíj (HUF)');
      setError(message);
      showError(message);
      setFieldErrors(prev => ({ ...prev, hourlyRate: message }));
      // jsdom-ban a `scrollIntoView` nem létezik — a `?.` NEM kozmetika, enélkül az őr elhasalna.
      hourlyRateInputRef.current?.scrollIntoView?.({ block: 'center' });
      hourlyRateInputRef.current?.focus?.();
      return;
    }

    // Validáció futtatása
    if (!validateForm()) {
      setError('Kérjük, töltse ki az összes kötelező mezőt helyesen!');
      return;
    }

    setSaving(true);
    try {
      if (isEditingOtherUser && editUserId) {
        await userProfileService.updateUserProfileById(editUserId, formData);
      } else {
        await userProfileService.updateMyProfile(formData);
      }
      // Toast, nem a lap tetején maradó Alert — hosszú űrlapon a felhasználó lent görget,
      // és a régi Alert a látómezőn kívül maradt (webes UX-elemzés 6.3 (b)).
      showSuccess('A profil módosítva.');
      setFieldErrors({});
      // A vázlat a MENTÉS SIKERÉRE nullázódik, nem az azt követő újratöltésre (az elnyeli a saját
      // hibáját) — különben egy elavult gépelt szöveg maradna a mezőben.
      setHourlyRateDraft(undefined);
      await loadProfile(); // Reload to show updated data
      onProfileSaved?.(); // Notify parent to refresh profile badge
    } catch (err: any) {
      // [validátor M-1, 2026-10-03] lejárt munkamenet → az újra-belépő ablakkal egyező mondat
      setError(saveErrorMessage(err, 'Hiba történt a profil frissítésekor'));
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('A fájl mérete maximum 5MB lehet');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Csak kép fájlok engedélyezettek');
      return;
    }

    setError('');
    setUploadingImage(true);

    try {
      let imageUrl: string;
      if (isEditingOtherUser && editUserId) {
        const result = await userProfileService.uploadProfileImageForUser(editUserId, file);
        imageUrl = result.imageUrl;
      } else {
        const result = await userProfileService.uploadProfileImage(file);
        imageUrl = result.imageUrl;
      }
      setProfile((prev) => (prev ? { ...prev, profileImageUrl: imageUrl } : null));
      showSuccess('A profilkép feltöltve.');
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt a kép feltöltésekor'));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageDelete = async () => {
    const confirmMessage = isEditingOtherUser
      ? `Biztosan törölni szeretnéd ${profile?.name} profil képét?`
      : 'Biztosan törölni szeretnéd a profil képed?';

    if (!(await confirm({
      title: 'Profilkép törlése',
      message: confirmMessage,
      confirmLabel: 'Törlés',
      destructive: true,
    }))) {
      return;
    }

    setUploadingImage(true);
    setError('');

    try {
      if (isEditingOtherUser && editUserId) {
        await userProfileService.deleteProfileImageForUser(editUserId);
      } else {
        await userProfileService.deleteProfileImage();
      }
      setProfile((prev) => (prev ? { ...prev, profileImageUrl: undefined } : null));
      showSuccess('A profilkép törölve.');
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt a kép törlésekor'));
    } finally {
      setUploadingImage(false);
    }
  };

  // Calculate age for conditional ParentName field
  const getAge = (): number | null => {
    // Calculate age based on formData.dateOfBirth (current form value)
    const dateOfBirth = formData.dateOfBirth || profile?.dateOfBirth;
    if (!dateOfBirth) return null;
    return ageOn(dateOfBirth, new Date());
  };

  const age = getAge();
  const isUnder18 = age !== null && age < 18;
  // A GÉPI szerepkör-névből [B-W4, 2026-09-16]: az óradíj- és bankszámla-mezők láthatósága nem
  // függhet egy ÁTNEVEZHETŐ címkétől. Régi válasz-alakon a `utils/roles.ts` visszaesik a display-re.
  const isCoach = isCoachRole(profile?.userRoleSystemName, profile?.userRoleName);

  /**
   * Mentetlen-változás BEJELENTÉSE. Az adatlap hosszú űrlap, a kilépési pontja viszont nem itt
   * van, hanem a Dashboard fül-váltásában — ezért a képernyő csak annyit tesz, hogy jelzi a
   * piszkosságát, a kérdést a kilépési pont teszi fel (`useUnsavedGuard`).
   * A hookot a `loading` / `!profile` korai visszatérések ELŐTT kell hívni (hook-sorrend).
   */
  // A nyitott jelszó-al-dialógus beírt mezői is mentetlen adat (lásd a `passwordFormDirty`
  // fölötti magyarázatot, BL-4) — enélkül az F5 kérdés nélkül eldobná őket.
  const isDirty = isFormDirty(baseline, formData) || (passwordDialogOpen && passwordFormDirty());
  useUnsavedChanges(isDirty, 'Adatlap');

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Profil nem található</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          {isEditingOtherUser ? `${profile.name} Adatlapja` : 'Adatlap'}
        </Typography>

        {isEditingOtherUser && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Admin módban szerkeszted: <strong>{profile.name}</strong>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Profile Image Section */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={profile.profileImageUrl}
              alt={profile.name}
              sx={{
                width: 150,
                height: 150,
                border: '3px solid',
                borderColor: 'primary.main',
              }}
            >
              {!profile.profileImageUrl && profile.name.charAt(0).toUpperCase()}
            </Avatar>

            {canEdit && (
              <>
                <IconButton
                  color="primary"
                  component="label"
                  disabled={uploadingImage}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 40,
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'background.paper' },
                  }}
                >
                  {uploadingImage ? <CircularProgress size={24} /> : <PhotoCameraIcon />}
                  <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                </IconButton>

                {profile.profileImageUrl && (
                  <IconButton
                    color="error"
                    disabled={uploadingImage}
                    onClick={handleImageDelete}
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'background.paper' },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </>
            )}
          </Box>
        </Box>

        {canEdit && (
          <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
            Kattints a kamera ikonra a kép feltöltéséhez (max 5MB)
          </Typography>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Profile Information */}
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Alapvető információk
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              required
              fullWidth
              label="Játékos neve"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onBlur={() => handleFieldBlur('name')}
              disabled={!canEdit || saving}
              error={!!fieldErrors.name}
              helperText={fieldErrors.name}
            />
            <TextField
              required
              fullWidth
              label="E-mail cím"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={() => handleFieldBlur('email')}
              disabled={!canEdit || saving}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
            />
            <TextField
              required
              fullWidth
              label="Telefonszám"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onBlur={() => handleFieldBlur('phone')}
              disabled={!canEdit || saving}
              error={!!fieldErrors.phone}
              helperText={fieldErrors.phone}
            />
            <TextField
              required
              fullWidth
              label="Születési dátum"
              name="dateOfBirth"
              type="date"
              value={formData.dateOfBirth}
              onChange={handleChange}
              onBlur={() => handleFieldBlur('dateOfBirth')}
              disabled={!canEdit || saving}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: DOB_MIN, max: dobMax() }}
              error={!!fieldErrors.dateOfBirth}
              helperText={fieldErrors.dateOfBirth || (age !== null ? `${age} éves` : '')}
            />
          </Box>

          {isUnder18 && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                18 év alatti játékosok esetén kötelező a szülő nevének és telefonszámának megadása
              </Alert>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  required
                  fullWidth
                  label="Szülő neve"
                  name="parentName"
                  value={formData.parentName}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur('parentName')}
                  disabled={!canEdit || saving}
                  error={!!fieldErrors.parentName}
                  helperText={fieldErrors.parentName}
                />
                <TextField
                  required
                  fullWidth
                  label="Szülő telefonszáma"
                  name="parentPhone"
                  value={formData.parentPhone}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur('parentPhone')}
                  disabled={!canEdit || saving}
                  error={!!fieldErrors.parentPhone}
                  helperText={fieldErrors.parentPhone}
                />
              </Box>
            </Box>
          )}

          {/* Preferred Trainings */}
          {groupTrainings.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Preferált edzések
              </Typography>
              {Object.entries(
                groupTrainings.reduce<Record<string, PreferredTrainingInfo[]>>((acc, t) => {
                  const venue = t.venueName || 'Nincs helyszín';
                  (acc[venue] = acc[venue] || []).push(t);
                  return acc;
                }, {})
              )
                .sort(([a], [b]) => a.localeCompare(b, 'hu'))
                .map(([venue, trainings]) => {
                  const selectedCount = trainings.filter(t => (formData.preferredTrainingIds || []).includes(t.plannedEventId)).length;
                  return (
                    <Accordion key={venue} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid', borderColor: 'divider', mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 0.5 } }}>
                        <LocationIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          {venue}
                        </Typography>
                        {selectedCount > 0 && (
                          <Typography variant="caption" sx={{ ml: 'auto', mr: 1, color: 'primary.main', fontWeight: 'bold' }}>
                            {selectedCount} kiválasztva
                          </Typography>
                        )}
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, px: { xs: 1, sm: 2 } }}>
                        <FormGroup>
                          {Object.entries(
                            trainings.reduce<Record<string, PreferredTrainingInfo[]>>((acc, t) => {
                              (acc[t.dayOfWeek] = acc[t.dayOfWeek] || []).push(t);
                              return acc;
                            }, {})
                          )
                            .sort(([a], [b]) => (DAY_ORDER[a] ?? 7) - (DAY_ORDER[b] ?? 7))
                            .map(([day, dayTrainings]) => (
                              <Box key={day} sx={{ mb: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                  {DAY_NAMES_HU[day] || day}
                                </Typography>
                                {dayTrainings.map((t) => (
                                  <Box key={t.plannedEventId} sx={{ display: 'flex', alignItems: 'flex-start', ml: 1 }}>
                                    <Checkbox
                                      checked={(formData.preferredTrainingIds || []).includes(t.plannedEventId)}
                                      onChange={() => handleTrainingToggle(t.plannedEventId)}
                                      disabled={!canEdit || saving}
                                      size="small"
                                      sx={{ p: 0.5 }}
                                    />
                                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, pt: 0.5 }}>
                                      {`${t.startTime}-${t.endTime} ${t.eventName}`}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            ))}
                        </FormGroup>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
            </Box>
          )}

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Számlázási adatok
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {isCoach && (
              <TextField
                fullWidth
                label="Óradíj (HUF)"
                name="hourlyRate"
                // `type="text"` + `inputMode="numeric"`, NEM `type="number"` [BL-2]: a szám-input a
                // `2.800` alakot érvényes számként adta át (`parseFloat` → 2,8 Ft). A megjelenítés
                // `forintFieldValue` (`?? ''`), NEM `|| ''` [N-12]: a tárolt 0 Ft ne tűnjön el.
                type="text"
                value={hourlyRateText()}
                inputProps={{ inputMode: 'numeric', 'data-testid': 'own-profile-hourly-rate-input' }}
                inputRef={hourlyRateInputRef}
                error={!!fieldErrors.hourlyRate}
                helperText={fieldErrors.hourlyRate}
                onChange={handleChange}
                disabled={!canEdit || saving}
              />
            )}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
            <TextField
              required
              fullWidth
              label="Számlázási név"
              name="billingName"
              value={formData.billingName}
              onChange={handleChange}
              disabled={!canEdit || saving}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                required
                label="Irányítószám"
                name="billingZipCode"
                value={formData.billingZipCode}
                onChange={handleChange}
                disabled={!canEdit || saving}
                sx={{ width: '30%' }}
              />
              <TextField
                required
                label="Város"
                name="billingCity"
                value={formData.billingCity}
                onChange={handleChange}
                disabled={!canEdit || saving}
                sx={{ width: '70%' }}
              />
            </Box>
            <TextField
              required
              fullWidth
              label="Utca, házszám"
              name="billingStreetAddress"
              value={formData.billingStreetAddress}
              onChange={handleChange}
              disabled={!canEdit || saving}
            />
            <TextField
              fullWidth
              label="Adószám (opcionális)"
              name="taxNumber"
              value={formData.taxNumber}
              onChange={handleChange}
              disabled={!canEdit || saving}
            />
          </Box>

          {canEdit && (
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {saving ? 'Mentés...' : 'Profil mentése'}
              </Button>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Account Information */}
        <Typography variant="h6" gutterBottom>
          Fiók információk
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Szerepkör:
            </Typography>
            {isAdmin && isEditingOtherUser && roles.length > 0 ? (
              <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                <Select
                  value={formData.userRoleId || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, userRoleId: e.target.value as string }))}
                  disabled={saving}
                >
                  {roles.map((role) => (
                    <MenuItem key={role.userRoleId} value={role.userRoleId}>
                      {role.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography variant="body1">{profile?.userRoleName}</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Preferált edzések:
            </Typography>
            <Typography variant="body1">
              {profile?.preferredTrainings && profile.preferredTrainings.length > 0
                ? profile.preferredTrainings.map(t => `${DAY_NAMES_HU[t.dayOfWeek] || t.dayOfWeek} ${t.startTime}-${t.endTime}`).join(', ')
                : 'Nincs megadva'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Regisztráció dátuma:
            </Typography>
            <Typography variant="body1">
              {/* [Web UX 1. csomag (4.3), 2026-10-03] UTC, „Z” nélkül → 00:00–02:00 között az előző napot mutatta. */}
              {formatServerDate(profile?.createdDate, { fallback: '-' })}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Fiók státusz:
            </Typography>
            <Typography variant="body1" color={profile?.isApproved ? 'success.main' : 'warning.main'}>
              {profile?.isApproved ? 'Jóváhagyva' : 'Jóváhagyásra vár'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Change Password Button - only for own profile */}
      {!isEditingOtherUser && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Button
            variant="outlined"
            startIcon={<LockIcon />}
            onClick={() => {
              setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
              setPasswordError('');
              setPasswordDialogOpen(true);
            }}
          >
            Jelszó módosítás
          </Button>
        </Paper>
      )}

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={closePasswordDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Jelszó módosítás</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {passwordError && <Alert severity="error">{passwordError}</Alert>}
            <TextField
              label="Jelenlegi jelszó"
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
              fullWidth
              autoComplete="current-password"
            />
            <TextField
              label="Új jelszó"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
              fullWidth
              autoComplete="new-password"
              // A SÚGÓ = A SZERVER MONDATA, szó szerint [B-W7, 2026-09-16]. Korábban „Minimum 6
              // karakter" állt itt, ami a 2026-09-15-i szigorítás után HIÁNYOS tájékoztatás volt.
              helperText={NEW_PASSWORD_RULE_MESSAGE}
            />
            <TextField
              label="Új jelszó megerősítés"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
              fullWidth
              autoComplete="new-password"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePasswordDialog} disabled={passwordSaving} data-testid="password-dialog-cancel">Mégse</Button>
          <Button
            onClick={handlePasswordChange}
            variant="contained"
            disabled={passwordSaving}
            startIcon={passwordSaving ? <CircularProgress size={20} /> : <LockIcon />}
          >
            {passwordSaving ? 'Mentés...' : 'Jelszó módosítás'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Notes Section - Only visible to Admin and Coach */}
      {profile && <UserNotes userId={profile.userId} userRoleName={profile.userRoleName} />}
    </Container>
  );
};

export default UserProfile;
